import {
    ApolloClient,
    ApolloLink,
    type NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import {
    createGatewayRetryLink,
    skipCacheDirective,
} from '@archipro-website/graphql-lib';
import type { ArchiproStage } from '@archipro-website/dev';
import { createApolloCache } from './apollo-cache';

const isBrowser = typeof window !== 'undefined';

/**
 * Singleton browser client; undefined during SSR and before first client init.
 */
let browserClient: ApolloClient<NormalizedCacheObject> | undefined;

const LOCAL_ENV_STAGES: ArchiproStage[] = ['Team', 'Localdev'];

function buildGraphqlUrl(): string {
    if (!isBrowser) {
        return '/graphql';
    }
    return `${window.location.protocol}//${window.location.host}/graphql`;
}

function buildOperationUri(
    operationName: string,
    baseUri: string,
    searchParams: URLSearchParams,
): string {
    const uri = new URL(baseUri);
    uri.searchParams.set('op', operationName);

    if (searchParams.has('purgecache')) {
        uri.searchParams.set(
            'purgecache',
            searchParams.get('purgecache') ?? '',
        );
    }

    return uri.toString();
}

interface BuildBrowserApolloClientOptions {
    stage: ArchiproStage;
}

/**
 * Creates the browser Apollo Client used by Remix hooks. Requests go to the
 * same-origin `/graphql` endpoint with session cookies included.
 */
function buildBrowserApolloClient({
    stage,
}: BuildBrowserApolloClientOptions): ApolloClient<NormalizedCacheObject> {
    const graphqlUrl = buildGraphqlUrl();
    const isLocalEnvironment = LOCAL_ENV_STAGES.includes(stage);

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
        if (
            graphQLErrors?.some((err) => err.extensions?.code === 'AUTH_ERROR')
        ) {
            window.location.href = window.location.origin;
            return;
        }

        if (networkError && operation.operationName !== 'LogError') {
            console.warn(
                '[Apollo networkError]',
                operation.operationName,
                networkError,
            );
        }
    });

    const operationUriLink = new ApolloLink((operation, forward) => {
        const currentUri =
            operation.getContext().uri ??
            buildOperationUri(
                operation.operationName,
                graphqlUrl,
                new URL(window.location.href).searchParams,
            );
        operation.setContext({ uri: currentUri });
        return forward(operation);
    });

    const uploadLink = createUploadLink({
        uri: graphqlUrl,
        credentials: 'include',
        headers: isLocalEnvironment
            ? { 'x-ap-local-environment': '1' }
            : undefined,
    });

    return new ApolloClient({
        cache: createApolloCache(),
        link: ApolloLink.from([
            errorLink,
            skipCacheDirective.requestHandler,
            operationUriLink,
            // Downstream of errorLink so a replayed request is reported once,
            // on its final outcome, rather than on every attempt, and directly
            // above the terminal link so nothing else re-runs per attempt.
            //
            // `retryBudgetMs` drops the replay once the first attempt has
            // already cost more than the retry can win back: a 504 is the
            // gateway reporting its own timeout, so replaying a slow one just
            // makes the user wait twice.
            createGatewayRetryLink({
                maxAttempts: 3,
                initialDelayMs: 300,
                maxDelayMs: 3000,
                retryBudgetMs: 10000,
            }),
            uploadLink,
        ]),
        connectToDevTools: import.meta.env.DEV,
        defaultOptions: {
            watchQuery: {
                errorPolicy: 'all',
            },
            query: {
                errorPolicy: 'all',
            },
            mutate: {
                errorPolicy: 'all',
            },
        },
        queryDeduplication: true,
    });
}

/**
 * Returns the singleton browser Apollo Client, creating it on first use.
 */
export function getBrowserApolloClient(
    options: BuildBrowserApolloClientOptions,
): ApolloClient<NormalizedCacheObject> {
    if (!isBrowser) {
        throw new Error(
            'getBrowserApolloClient can only be called in the browser',
        );
    }

    if (!browserClient) {
        browserClient = buildBrowserApolloClient(options);
    }

    return browserClient;
}

/**
 * Clears the in-memory Apollo store. Call on logout or when chat state must
 * be discarded for the previous user.
 */
export async function resetBrowserApolloClientStore(): Promise<void> {
    await browserClient?.clearStore();
}

/**
 * Test helper to inject a mocked client in Vitest.
 */
export function setBrowserApolloClientForTests(
    client: ApolloClient<NormalizedCacheObject>,
): void {
    if (!import.meta.env.TEST) {
        throw new Error(
            'setBrowserApolloClientForTests is only available in tests',
        );
    }
    browserClient = client;
}
